import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { profileService } from '@chartai/firebase';

import { profileKeys } from '../queryKeys';

export function useProfiles(uid: string) {
  return useQuery({
    queryKey: profileKeys.list(uid),
    queryFn: async () => {
      return profileService.getActiveProfiles(uid);
    },
    enabled: Boolean(uid),
  });
}

export function useAllProfiles(uid: string) {
  return useQuery({
    queryKey: [...profileKeys.lists(), uid, 'all'],
    queryFn: async () => {
      return profileService.getAllProfiles(uid);
    },
    enabled: Boolean(uid),
  });
}

export function useProfile(uid: string, profileId: string) {
  return useQuery({
    queryKey: profileKeys.detail(profileId),
    queryFn: async () => {
      return profileService.getProfile(uid, profileId);
    },
    enabled: Boolean(uid) && Boolean(profileId),
  });
}

export function useCreateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { uid: string; displayName: string; email?: string }) => {
      return profileService.createProfile(input.uid, {
        displayName: input.displayName,
        ...(input.email ? { email: input.email } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { uid: string; profileId: string; data: Record<string, unknown> }) => {
      return profileService.updateProfile(input.uid, input.profileId, input.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
    },
  });
}

export function useDeactivateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { uid: string; profileId: string; inactive: boolean }) => {
      return profileService.updateProfile(input.uid, input.profileId, { inactive: input.inactive });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
    },
  });
}

export function useTerminateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { uid: string; profileId: string }) => {
      return profileService.updateProfile(input.uid, input.profileId, { isTerminated: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.lists() });
    },
  });
}
